import React, { createContext, useContext, useRef, ReactNode, useMemo } from 'react';

interface ErrorFieldContextType {
  registerErrorField: (fieldRef: HTMLDivElement | HTMLLabelElement | null) => void;
  unregisterErrorField: (fieldRef: HTMLDivElement | HTMLLabelElement | null) => void;
  scrollToFirstError: () => void;
}

const ErrorFieldContext = createContext<ErrorFieldContextType | undefined>(undefined);

interface ErrorFieldProviderProps {
  children: ReactNode;
}

export const ErrorFieldProvider: React.FC<ErrorFieldProviderProps> = ({ children }) => {
  const errorFieldsRefs = useRef<(HTMLDivElement | HTMLLabelElement)[]>([]);
  const registerErrorField = (fieldRef: HTMLDivElement | HTMLLabelElement | null) => {
    if (fieldRef && !errorFieldsRefs.current.includes(fieldRef)) {
      errorFieldsRefs.current.push(fieldRef);
    }
  };

  const unregisterErrorField = (fieldRef: HTMLDivElement | HTMLLabelElement | null) => {
    if (fieldRef) {
      errorFieldsRefs.current = errorFieldsRefs.current.filter(ref => ref !== fieldRef);
    }
  };

  const scrollToFirstError = () => {
    if (errorFieldsRefs.current.length > 0) {
      const highestErrorField = errorFieldsRefs.current.reduce((highest, current) => {
        const currentTop = current.getBoundingClientRect().top;
        return currentTop < highest.getBoundingClientRect().top ? current : highest;
      }, errorFieldsRefs.current[0]);

      highestErrorField.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const providerValue = useMemo(
    () => ({ registerErrorField, unregisterErrorField, scrollToFirstError }),
    [registerErrorField, scrollToFirstError],
  );

  return <ErrorFieldContext.Provider value={providerValue}>{children}</ErrorFieldContext.Provider>;
};

export const useErrorFieldContext = (): ErrorFieldContextType => {
  const context = useContext(ErrorFieldContext);
  if (!context) {
    throw new Error('useErrorFieldContext must be used within an ErrorFieldProvider');
  }
  return context;
};
